import { useDashboard } from '@cobliteam/dashboard/src/providers/DashboardProvider';
import { DatadogTrack } from '@cobliteam/datadog-rum';
import useGlobalFilterStore from '@cobliteam/hooks/src/useGlobalFilterStore';
import { useSearchData } from '@cobliteam/hooks/src/useSearchStore';
import { useLanguage, useTranslation } from '@cobliteam/i18n';
import { Button } from '@cobliteam/ui/components';
import { InputSearch } from '@cobliteam/ui/form';
import Icons from '@cobliteam/ui/icons';
import { cn } from '@cobliteam/ui/utils';
import { getTextForLanguage } from '@cobliteam/ui-business/utils';
import { Suspense, useCallback, useEffect, useMemo } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useLocation } from 'react-router-dom';
import GlobalSearchContent from './containers/GlobalSearchContent';
import { useGlobalSidebarDef } from './globalSidebarDef';
import { GlobalSearchProvider } from './provider/GlobalSearchProvider';
import { ProviderPersistQueryClient } from './provider/PersisterProvider';
import { type Category, type DataWithCategory, EnumCategory, type PageWithCategory } from './types';

const INPUT_SEARCH_CLASS = 'w-[300px]';

const GlobalSearchFallback = () => {
  return (
    <InputSearch onChange={() => ''} size='sm' className={cn(INPUT_SEARCH_CLASS, '!border-warning-6 !bg-warning-2')} placeholder='Erro ao carregar a busca global...' disabled />
  );
};

const GlobalSearchLoading = () => {
  return <InputSearch onChange={() => ''} loading size='sm' className={cn(INPUT_SEARCH_CLASS)} placeholder='Carregando a busca global...' disabled />;
};

const GlobalSearchError = () => {
  return (
    <div className='flex items-center justify-center'>
      <Button variant='surface' size='sm' color='danger' icon={<Icons name='x' />} />
    </div>
  );
};

const GlobalSearchTrigger = () => {
  const { t } = useTranslation('global-search');
  const { currentLanguage } = useLanguage();
  const location = useLocation().pathname;
  const vehicleGroupIds = useGlobalFilterStore((state: any) => state.vehicleGroupIds);
  const { sidebar } = useDashboard();
  const globalSidebarDef = useGlobalSidebarDef(t);

  const getCategory = useCallback(
    (categoryId: string) => {
      const group = globalSidebarDef.find((category) => category.categories.find((item) => item.id === categoryId));
      return group?.categories.find((item) => item.id === categoryId);
    },
    [globalSidebarDef]
  );
  const pagesCategory = useMemo(() => getCategory(EnumCategory.PAGES), [getCategory]);

  const pagesWithCategory = useMemo<PageWithCategory[]>(() => {
    if (!pagesCategory) return [];

    return sidebar.pagesFlatted
      .filter((page) => !page.sidebar?.hide)
      .filter((page) => page.url)
      .map((page) => {
        const pageTitle = getTextForLanguage(page.title, currentLanguage);
        const pageDescription = getTextForLanguage(page.description, currentLanguage);

        return {
          ...page,
          id: pageTitle?.toLowerCase().replace(/\s+/g, '-') || '',
          primary: page.primary || '',
          category: pagesCategory,
          description: pageDescription,
        };
      });
  }, [currentLanguage, pagesCategory, sidebar.pagesFlatted]);

  const { isLoading, fuse, items, isRefetching, error, refresh } = useSearchData<Category, PageWithCategory>({
    currentLocation: location,
    vehicleGroupIds,
    pagesData: pagesWithCategory,
    getCategory,
  });

  useEffect(() => {
    if (vehicleGroupIds.length > 0) refresh?.();
  }, [refresh, vehicleGroupIds]);

  if (isLoading) return <GlobalSearchLoading />;
  if (!(fuse && items)) return <GlobalSearchFallback />;
  if (error) return <GlobalSearchError />;

  return (
    <GlobalSearchProvider fuse={fuse} data={items as DataWithCategory[]}>
      <GlobalSearchContent handleUpdateData={refresh} isRefetching={isRefetching} />
    </GlobalSearchProvider>
  );
};

const GlobalSearch = () => {
  const { runtimeFlags } = useDashboard();
  const hasValidGroupOrVehicles = !(runtimeFlags.noValidGroupOrVehicles ?? false);
  if (!hasValidGroupOrVehicles) return null;

  return (
    <ErrorBoundary
      fallback={<GlobalSearchError />}
      onError={(error, info) => {
        DatadogTrack.error(error, { component: 'GlobalSearch', info });
      }}
    >
      <Suspense fallback={<GlobalSearchLoading />}>
        <ProviderPersistQueryClient>
          <GlobalSearchTrigger />
        </ProviderPersistQueryClient>
      </Suspense>
    </ErrorBoundary>
  );
};

export default GlobalSearch;
